import { ACTIVATION_ROW_LABEL } from '@/components/matrix/matrix-labels';
import {
  LEVIER_BORDER_CLASSES,
  LEVIER_SOLID_CLASSES,
  LEVIER_TINT_CLASSES,
  type LevierColorTreatment,
  levierColorTokenFor,
} from '@/lib/display-tweaks';
import { cn } from '@/lib/utils';
import { Icon } from '@pms-digital/design-system';
import { FAKE_COMPANY, FAKE_LEVIER_NAMES, FAKE_LEVIER_ORDERS } from './fake-company';
import { SkeletonBar } from './skeleton-bar';

interface ActivationBlockProps {
  /** Value of `matrix-activation-levier-names`: `hidden`, `column` or `inline`. */
  names: string;
  /** What the colours paint, already resolved against the names (`resolveLevierColorTreatment`). */
  treatment: LevierColorTreatment;
}

const GRID_COLUMNS: Record<string, string> = {
  hidden: 'grid-cols-[128px_1fr_1fr]',
  column: 'grid-cols-[104px_140px_1fr_1fr]',
  inline: 'grid-cols-[22px_140px_1fr_1fr]',
};

/**
 * The six activation rows of a matrix: one merged group cell on the left,
 * optionally one name cell per levier, then two body cells per row. Shared by
 * the "names" and "colours" miniatures.
 */
export function ActivationBlock({ names, treatment }: ActivationBlockProps) {
  const hasNames = names !== 'hidden';
  const isStrip = names === 'inline';
  return (
    <div className={cn('grid text-body leading-tight', GRID_COLUMNS[names] ?? GRID_COLUMNS.hidden)}>
      <div
        className={cn(
          'relative row-span-6 bg-gray-50 py-2 font-bold text-gray-800',
          isStrip ? 'px-0 text-center text-gray-500' : 'pr-2 pl-3',
          hasNames ? 'border-gray-100 border-r' : 'border-gray-200 border-r-2',
        )}
      >
        {treatment === 'bar' && (
          <span className="absolute top-0 bottom-0 left-0 flex w-1.5 flex-col">
            {FAKE_LEVIER_ORDERS.map((order) => (
              <span
                key={order}
                className={cn(
                  'flex-1',
                  LEVIER_SOLID_CLASSES[levierColorTokenFor(FAKE_COMPANY.noyau, order)],
                )}
              />
            ))}
          </span>
        )}
        {isStrip ? (
          <span className="flex justify-center">
            <Icon name="expand_more" size={16} />
          </span>
        ) : (
          ACTIVATION_ROW_LABEL
        )}
      </div>
      {FAKE_LEVIER_ORDERS.map((order, index) => {
        const token = levierColorTokenFor(FAKE_COMPANY.noyau, order);
        return (
          <ActivationRow
            key={order}
            name={hasNames ? (FAKE_LEVIER_NAMES[index] ?? '') : null}
            treatment={treatment}
            tintClass={LEVIER_TINT_CLASSES[token]}
            borderClass={LEVIER_BORDER_CLASSES[token]}
            dotClass={LEVIER_SOLID_CLASSES[token]}
          />
        );
      })}
    </div>
  );
}

interface ActivationRowProps {
  /** The levier name, or `null` when there is no name column. */
  name: string | null;
  treatment: LevierColorTreatment;
  tintClass: string;
  borderClass: string;
  dotClass: string;
}

function ActivationRow({ name, treatment, tintClass, borderClass, dotClass }: ActivationRowProps) {
  const cell = 'flex h-8.5 flex-col justify-center border-gray-100 border-r border-b px-2';
  return (
    <>
      {name !== null && (
        <div
          className={cn(
            'flex h-8.5 items-center gap-1 overflow-hidden whitespace-nowrap border-gray-100 border-r-2 border-r-gray-200 border-b bg-gray-50 px-2 font-bold text-noir',
            treatment === 'tint' && tintClass,
            treatment === 'accent' && ['border-l-4', borderClass],
          )}
        >
          {treatment === 'accent' && (
            <span className={cn('h-2 w-2 shrink-0 rounded-full', dotClass)} />
          )}
          {name}
        </div>
      )}
      <div className={cell}>
        <SkeletonBar width={85} />
      </div>
      <div className={cell}>
        <SkeletonBar width={70} />
      </div>
    </>
  );
}
